// Behavioral Continuity Engine
// Surfaces quiet, observational behavioral patterns from a user's rehearsal history.
// No scoring. No analytics energy. Just calmly noticed tendencies.

import { supabase } from "@/integrations/supabase/client";

export interface RehearsalMessage {
  turn: number;
  role: "agent" | "user";
  content: string;
  tactic_tag: string | null;
}

export interface RehearsalRecord {
  id: string;
  created_at: string;
  entry_type: string;
  messages: RehearsalMessage[];
  tactics_sequence: { turn: number; tactic: string }[] | null;
}

export interface BehavioralObservation {
  label: string;        // e.g. "Conversation Shift"
  observation: string;  // the quiet sentence
  continuity?: string;  // optional cross-session continuity line
}

const wc = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

const userTurns = (msgs: RehearsalMessage[]) => msgs.filter((m) => m.role === "user");

const avg = (nums: number[]) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0);

// Detect softening cues: hedges, repair, justification
const SOFTENING = /\b(sorry|i didn'?t mean|i just|maybe|perhaps|i guess|kind of|sort of|i was trying|let me explain)\b/i;
const REPAIR = /\b(sorry|apologi|i didn'?t mean|i shouldn'?t have|forgive|let me make|fix this)\b/i;
const OVER_EXPLAIN_HINT = /\b(because|the reason|what i meant|let me clarify|to be fair|technically)\b/i;
const DEFENSIVE = /\b(no i didn'?t|that'?s not true|you'?re wrong|stop|don'?t)\b/i;

/**
 * Analyse a single rehearsal in the context of prior history.
 * Returns 1–3 observations. Empty array if not enough signal.
 */
export function analyzeBehavioralContinuity(
  current: RehearsalRecord,
  history: RehearsalRecord[]
): BehavioralObservation[] {
  const observations: BehavioralObservation[] = [];
  const replies = userTurns(current.messages);
  if (replies.length < 2) return observations;

  const lengths = replies.map((m) => wc(m.content));
  const firstHalf = lengths.slice(0, Math.ceil(lengths.length / 2));
  const secondHalf = lengths.slice(Math.ceil(lengths.length / 2));
  const firstAvg = avg(firstHalf);
  const secondAvg = avg(secondHalf);

  // 1. Response shortening under sustained pressure
  if (firstAvg > 0 && secondAvg < firstAvg * 0.6 && firstAvg >= 8) {
    observations.push({
      label: "Conversation Shift",
      observation: "Your responses became shorter as the exchange progressed.",
    });
  } else if (firstAvg > 0 && secondAvg > firstAvg * 1.6) {
    observations.push({
      label: "Escalation Response",
      observation: "Your replies grew longer as pressure increased — a tendency toward over-explaining.",
    });
  }

  // 2. Softening / boundary instability
  const softeningCount = replies.filter((m) => SOFTENING.test(m.content)).length;
  if (softeningCount >= 2 && softeningCount / replies.length >= 0.4) {
    observations.push({
      label: "Boundary Pattern",
      observation: "You tend to soften your position after pushback.",
    });
  }

  // 3. Repair before assertion
  const earlyRepair = replies.slice(0, 2).some((m) => REPAIR.test(m.content));
  if (earlyRepair) {
    observations.push({
      label: "Observed Pattern",
      observation: "You attempt repair before asserting your position.",
    });
  }

  // 4. Over-explaining
  const overExplain = replies.filter((m) => OVER_EXPLAIN_HINT.test(m.content)).length;
  if (overExplain >= 2) {
    observations.push({
      label: "Behavioral Tendency",
      observation: "You frequently provide justification after emotional pressure.",
    });
  }

  // 5. Defensive lean (workplace especially)
  const defensive = replies.filter((m) => DEFENSIVE.test(m.content)).length;
  if (defensive >= 2 && current.entry_type === "professional") {
    observations.push({
      label: "Escalation Response",
      observation: "Defensive framing surfaced more in this professional simulation.",
    });
  }

  // ─── Cross-session continuity ───
  if (history.length >= 2) {
    const histReplies = history.flatMap((h) => userTurns(h.messages).map((m) => wc(m.content)));
    const histAvg = avg(histReplies);
    const curAvg = avg(lengths);

    if (histAvg > 0 && curAvg < histAvg * 0.7 && observations.length < 3) {
      observations.push({
        label: "Compared to recent rehearsals",
        observation: "Your responses were more contained than in earlier sessions.",
        continuity: `Historic average: ${Math.round(histAvg)} words · This session: ${Math.round(curAvg)}`,
      });
    } else if (histAvg > 0 && curAvg > histAvg * 1.4 && observations.length < 3) {
      observations.push({
        label: "Compared to recent rehearsals",
        observation: "You wrote more than usual under this pressure pattern.",
      });
    }

    // Recurring tactic across history
    const allTactics = history
      .flatMap((h) => (h.tactics_sequence || []).map((t) => t.tactic))
      .filter(Boolean);
    const counts = allTactics.reduce<Record<string, number>>((acc, t) => {
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (top && top[1] >= 3 && observations.length < 3) {
      observations.push({
        label: "Recurring Pattern",
        observation: `${top[0]} has appeared across several of your rehearsals.`,
      });
    }
  }

  return observations.slice(0, 3);
}

/** Fetch the user's prior rehearsal history (excluding the current one). */
export async function fetchRehearsalHistory(
  userId: string,
  excludeId: string,
  limit = 10
): Promise<RehearsalRecord[]> {
  const { data, error } = await supabase
    .from("rehearsal_sessions")
    .select("id, created_at, entry_type, messages, tactics_sequence")
    .eq("user_id", userId)
    .eq("status", "completed")
    .neq("id", excludeId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data) return [];
  return data.map((d) => ({
    id: d.id,
    created_at: d.created_at,
    entry_type: d.entry_type,
    messages: (d.messages as unknown as RehearsalMessage[]) || [],
    tactics_sequence:
      (d.tactics_sequence as unknown as { turn: number; tactic: string }[]) || null,
  }));
}

/** Lightweight ambient observation for the Practice homepage. Single sentence or null. */
export async function fetchAmbientObservation(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("rehearsal_sessions")
    .select("id, created_at, entry_type, messages, tactics_sequence")
    .eq("user_id", userId)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(8);

  if (error || !data || data.length < 2) return null;

  const records: RehearsalRecord[] = data.map((d) => ({
    id: d.id,
    created_at: d.created_at,
    entry_type: d.entry_type,
    messages: (d.messages as unknown as RehearsalMessage[]) || [],
    tactics_sequence:
      (d.tactics_sequence as unknown as { turn: number; tactic: string }[]) || null,
  }));

  const obs = analyzeBehavioralContinuity(records[0], records.slice(1));
  return obs[0]?.observation || null;
}
